import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import { LocationListItem } from '../api/types';

const useLocations = (billingAccountId: number | null) => {
  const enabled = billingAccountId != null;
  const queryClient = useQueryClient();
  const query = useQuery<LocationListItem[]>({
    queryKey: enabled
      ? orderV2QueryKeys.locations(billingAccountId)
      : ['orders_v2', 'locations', 'disabled'],
    queryFn: () =>
      createOrderV2Client
        .listLocations(billingAccountId as number)
        .then((result) => result || []),
    enabled,
  });

  // Mirrors the legacy `refreshLocations()` helper: callers invoke this
  // after creating a new location so the dropdown picks it up. We
  // invalidate the query rather than refetching the result inline so the
  // single source of truth is the cache, and any other consumer of the
  // same key gets the updated value too.
  const refreshLocations = useCallback(async () => {
    if (!enabled) return;
    await queryClient.invalidateQueries({
      queryKey: orderV2QueryKeys.locations(billingAccountId),
    });
  }, [enabled, billingAccountId, queryClient]);

  return {
    locations: query.data ?? [],
    refreshLocations,
  };
};

export default useLocations;
